import { MouseEvent, ReactNode, useContext, useEffect, useState } from "react";
import { useI18n } from "i18n";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { PageLoader } from "@remo-co/ui-core/src/components/PageLoader";
import { ChevronLeft } from "@remo-co/ui-core/src/icons/ChevronLeft";
import logger from "logging/logger";
import "./UserDetailsToRegister.scss";
import { validateURL } from "helpers/urlHelper";
import { Events, TRACKING_CONTEXT } from "modules/tracking";
import { USERPILOT_CONTEXT } from "modules/userpilot/UserpilotContext";
import useRegisterToEvent from "modules/event/hooks/useRegisterToEvent";
import { useCheckEventStatus } from "modules/event/hooks/useCheckEventStatus";
import { IEvent } from "modules/event/types";
import { IUser } from "modules/app/types";
import { getMethodsFromUserData } from "modules/auth/utils/getMethodsFromUserData";
import { useAppDispatch } from "store/hooks";
import { showUserPreferredLoginBypass } from "modules/auth/redux/authSlice";
import { addWarningNotification } from "modules/notification/redux/notificationSlice";
import { useStyles } from "./styles";
import ForgetPassword from "../ForgetPassword";
import SignUp from "../SignUp";
import SignIn from "../SignIn";

export const getWebsite = (
  website: string | (string | null)[] | null,
): string | undefined => {
  if (website && validateURL(`${website}`)) {
    if (typeof website === "string") {
      return website as string;
    }
  }

  return undefined;
};

export interface UserDetailsToRegisterProps {
  event: IEvent;
  initialName?: string;
  email: string;
  onComplete: (authCompleted: boolean) => void;
  login?: boolean;
  customLoading?: ReactNode;
}

const UserDetailsToRegister = ({
  event,
  initialName = "",
  email,
  onComplete,
  login,
  customLoading,
}: UserDetailsToRegisterProps) => {
  const styles = useStyles();
  const { t } = useI18n(["common", "eventLanding", "auth", "event"]);
  const [emailUser, setEmailUser] = useState<IUser | null>(null);
  const [isDoingAuth, setIsDoingAuth] = useState(true);
  const [emailInput, setEmailInput] = useState(email);
  const [isSigningUp, setIsSigningUp] = useState(false);
  const [showForgotPasswordForm, setShowForgotPasswordForm] = useState(false);
  const { getUserDetailsByEmail } = useRegisterToEvent();
  const dispatch = useAppDispatch();
  const { checkIfEventHappeningNow } = useCheckEventStatus();
  const isHappeningNow = checkIfEventHappeningNow(event);
  const { track } = useContext(TRACKING_CONTEXT);
  const { updateUserpilotIdentity } = useContext(USERPILOT_CONTEXT);

  const getUser = async () => {
    setIsDoingAuth(true);

    let userData;

    if (email) {
      userData = await getUserDetailsByEmail(email);
    }

    if (userData) {
      const methods = getMethodsFromUserData(userData);

      if (methods.length > 1 || userData.saml) {
        dispatch(showUserPreferredLoginBypass(methods));
        dispatch(
          addWarningNotification({
            message: t("event:preferred.login.nudge"),
          }),
        );
        onComplete(false);
      } else {
        setEmailUser(userData);
      }
    } else if (login) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error setting this to null causes unintended behavior. Consider refactoring.
      setEmailUser({});
    }

    setIsDoingAuth(false);
  };

  useEffect(() => {
    getUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isDoingAuth) {
      return;
    }

    track(
      emailUser
        ? Events.LOGIN_VIEWED
        : Events.EVENT_REGISTRATION_CREATE_ACCOUNT,
    );

    if (emailUser) {
      updateUserpilotIdentity({
        id: `${emailUser.id}`,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error this isn't referenced anywhere else, even in the server.
        last_seen: (new Date().getTime() / 1000).toFixed(0),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDoingAuth, emailUser]);

  if (isDoingAuth || isSigningUp) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    if (customLoading) return <>{customLoading}</>;

    return (
      <div className={styles.container}>
        <PageLoader
          message={`${
            isSigningUp ? t("wait") : t("eventLanding:getting.details.message")
          }`}
        />
      </div>
    );
  }

  const showFPForm = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setShowForgotPasswordForm(true);
  };

  const onBackClick = () => {
    logger.info("[SignIn][onBackClick]");

    if (showForgotPasswordForm) {
      setShowForgotPasswordForm(false);

      return;
    }
    onComplete(false);
  };

  const renderAuthText = () => {
    if (showForgotPasswordForm) {
      return t("eventLanding:recover.password");
    }

    if (isHappeningNow && emailUser) {
      return t("eventLanding:login.your.account");
    }
    if (isHappeningNow) {
      return t("eventLanding:create.account");
    }

    if (emailUser) {
      return t("eventLanding:login.your.account");
    }

    return t("eventLanding:create.account");
  };

  const getForm = () => {
    if (emailUser)
      return (
        <SignIn
          event={event}
          email={emailInput}
          setEmail={setEmailInput}
          setIsSigningUp={setIsSigningUp}
          showFPForm={showFPForm}
        />
      );

    return (
      <SignUp
        event={event}
        email={emailInput}
        setEmail={setEmailInput}
        initialName={initialName}
        setIsSigningUp={setIsSigningUp}
      />
    );
  };

  return (
    <div>
      <Button
        onClick={onBackClick}
        className={styles.backButton}
        data-testid="back-button"
        variant="text"
        color="dark"
      >
        <ChevronLeft className={styles.backButtonIcon} /> {t("button.back")}
      </Button>
      <div>
        <Typography className={styles.title} variant="h4">
          {renderAuthText()}
        </Typography>
      </div>

      {showForgotPasswordForm ? (
        <ForgetPassword
          eventCode={event.code}
          emailInput={emailInput}
          setEmailInput={setEmailInput}
        />
      ) : (
        getForm()
      )}
    </div>
  );
};

export default UserDetailsToRegister;
